/**
 * Bundle Quick View Web Component
 * Standard native HTML Custom Element extending HTMLElement
 * Pure Liquid-first architecture: Toggles Liquid-rendered product cards & image galleries
 */

if (!customElements.get('bundle-quick-view')) {
  class BundleQuickView extends HTMLElement {
    constructor() {
      super();
      this.closeModal = this.closeModal.bind(this);
      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.lastActiveElement = null;
    }

    connectedCallback() {
      this.bindCloseEvents();
      document.addEventListener('keydown', this.handleKeyDown);
      document.addEventListener('focusin', this.handleFocusIn);
      document.addEventListener('click', this.handleDocumentClick, true);
    }

    disconnectedCallback() {
      clearTimeout(this._focusTimer);
      this.unbindCloseEvents();
      document.removeEventListener('keydown', this.handleKeyDown);
      document.removeEventListener('focusin', this.handleFocusIn);
      document.removeEventListener('click', this.handleDocumentClick, true);
    }

    bindCloseEvents() {
      const closeElements = this.querySelectorAll('[data-bundle-qv-close]');
      closeElements.forEach(el => el.addEventListener('click', this.closeModal));
    }

    unbindCloseEvents() {
      const closeElements = this.querySelectorAll('[data-bundle-qv-close]');
      closeElements.forEach(el => el.removeEventListener('click', this.closeModal));
    }

    initCardSliders(card) {
      if (!card) return;
      const jQuery = window.jQuery;
      if (!jQuery || !jQuery.fn || !jQuery.fn.slick) {
        return;
      }

      const stageEl = card.querySelector('.bundle-qv-image-stage');
      const thumbsEl = card.querySelector('.bundle-qv-thumbnails');
      if (!stageEl) return;

      const mainImages = stageEl.querySelectorAll('.bundle-qv-main-img');
      if (mainImages.length <= 1) {
        this.updateSlideAccessibility(card);
        return;
      }

      const stageJQuery = jQuery(stageEl);
      const thumbsJQuery = thumbsEl ? jQuery(thumbsEl) : null;

      if (!stageEl.classList.contains('slick-initialized')) {
        stageJQuery.on('init reInit afterChange', (event, slick, currentSlide) => {
          if (typeof currentSlide === 'number' && thumbsEl && thumbsEl.classList.contains('slick-initialized') && thumbsJQuery) {
            const targetSlide = thumbsEl.querySelector(`.slick-slide[data-slick-index="${currentSlide}"]`);
            if (targetSlide && !targetSlide.classList.contains('slick-active')) {
              const targetPage = Math.floor(currentSlide / 4) * 4;
              thumbsJQuery.slick('slickGoTo', targetPage);
            }
          }
          this.updateSlideAccessibility(card);
        });

        const stagePrevBtn = card.querySelector('.bundle-qv-image-stage .bundle-qv-prev-btn');
        const stageNextBtn = card.querySelector('.bundle-qv-image-stage .bundle-qv-next-btn');

        stageJQuery.slick({
          slide: '.bundle-qv-main-img',
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: true,
          prevArrow: stagePrevBtn,
          nextArrow: stageNextBtn,
          infinite: false,
          accessibility: false,
          speed: 250,
          adaptiveHeight: false
        });
      }

      if (thumbsEl && !thumbsEl.classList.contains('slick-initialized') && thumbsJQuery) {
        thumbsJQuery.on('init reInit beforeChange afterChange', () => {
          this.updateSlideAccessibility(card);
        });

        const thumbPrevBtn = card.querySelector('.bundle-qv-thumbnails-wrap .bundle-qv-thumb-prev-btn');
        const thumbNextBtn = card.querySelector('.bundle-qv-thumbnails-wrap .bundle-qv-thumb-next-btn');

        thumbsJQuery.slick({
          slidesToShow: 4,
          slidesToScroll: 4,
          dots: false,
          arrows: true,
          prevArrow: thumbPrevBtn,
          nextArrow: thumbNextBtn,
          focusOnSelect: false,
          infinite: false,
          accessibility: false,
          speed: 250,
          responsive: [
            {
              breakpoint: 768,
              settings: {
                slidesToShow: 4,
                slidesToScroll: 4
              }
            }
          ]
        });

        thumbsEl.addEventListener('click', (e) => {
          const thumbBtn = e.target.closest('.bundle-qv-thumb-btn');
          if (!thumbBtn) return;
          e.preventDefault();
          const index = parseInt(thumbBtn.getAttribute('data-index') || 0, 10);
          if (stageEl.classList.contains('slick-initialized')) {
            stageJQuery.slick('slickGoTo', index);
          }
        });
      }

      // Prevent browser native scroll from distorting slick track
      const slickLists = card.querySelectorAll('.slick-list');
      slickLists.forEach(slickList => {
        slickList.addEventListener('scroll', () => {
          slickList.scrollLeft = 0;
        });
      });

      requestAnimationFrame(() => {
        if (stageEl.classList.contains('slick-initialized')) {
          stageJQuery.slick('setPosition');
          stageJQuery.slick('slickGoTo', 0, true);
        }
        if (thumbsEl && thumbsEl.classList.contains('slick-initialized') && thumbsJQuery) {
          thumbsJQuery.slick('setPosition');
          thumbsJQuery.slick('slickGoTo', 0, true);
        }
        this.updateSlideAccessibility(card);
      });
    }

    updateSlideAccessibility(card) {
      if (!card) return;
      const jQuery = window.jQuery;

      const stageEl = card.querySelector('.bundle-qv-image-stage');
      const thumbsEl = card.querySelector('.bundle-qv-thumbnails');

      if (stageEl && stageEl.classList.contains('slick-initialized')) {
        stageEl.setAttribute('role', 'region');
        stageEl.setAttribute('aria-roledescription', 'carousel');
        stageEl.removeAttribute('aria-live');

        const mainSlides = stageEl.querySelectorAll('.slick-slide');
        mainSlides.forEach(slide => {
          const isCurrent = slide.classList.contains('slick-current');
          const img = slide.querySelector('.bundle-qv-main-img');

          slide.setAttribute('role', 'group');
          slide.setAttribute('aria-roledescription', 'slide');

          if (isCurrent) {
            slide.setAttribute('aria-hidden', 'false');
            slide.removeAttribute('inert');
            slide.setAttribute('tabindex', '-1');
            if (img) {
              img.setAttribute('tabindex', '-1');
              img.removeAttribute('aria-hidden');
              img.classList.add('is-active');
            }
          } else {
            slide.setAttribute('aria-hidden', 'true');
            slide.setAttribute('inert', '');
            slide.setAttribute('tabindex', '-1');
            if (img) {
              img.setAttribute('tabindex', '-1');
              img.setAttribute('aria-hidden', 'true');
              img.classList.remove('is-active');
            }
          }
        });
      }

      if (thumbsEl && thumbsEl.classList.contains('slick-initialized')) {
        let stageIndex = 0;
        if (stageEl && stageEl.classList.contains('slick-initialized') && jQuery) {
          stageIndex = jQuery(stageEl).slick('slickCurrentSlide');
        }

        const thumbSlides = thumbsEl.querySelectorAll('.slick-slide');
        thumbSlides.forEach(slide => {
          const slideIndex = parseInt(slide.getAttribute('data-slick-index'), 10);
          const isVisible = slide.classList.contains('slick-active');
          const isCurrent = slideIndex === stageIndex;
          const btn = slide.querySelector('.bundle-qv-thumb-btn');

          if (isVisible) {
            slide.setAttribute('aria-hidden', 'false');
            slide.removeAttribute('inert');
            slide.setAttribute('tabindex', '-1');
            if (btn) {
              btn.setAttribute('tabindex', '0');
              btn.removeAttribute('aria-hidden');
            }
          } else {
            slide.setAttribute('aria-hidden', 'true');
            slide.setAttribute('inert', '');
            slide.setAttribute('tabindex', '-1');
            if (btn) {
              btn.setAttribute('tabindex', '-1');
              btn.setAttribute('aria-hidden', 'true');
            }
          }

          if (btn) {
            if (isCurrent) {
              btn.classList.add('is-active');
              btn.setAttribute('aria-current', 'true');
            } else {
              btn.classList.remove('is-active');
              btn.removeAttribute('aria-current');
            }
          }
        });
      }

      const arrows = card.querySelectorAll('.bundle-qv-nav-btn, .bundle-qv-thumb-nav-btn');
      arrows.forEach(arrow => {
        if (arrow.classList.contains('slick-disabled')) {
          arrow.setAttribute('aria-disabled', 'true');
          arrow.setAttribute('tabindex', '-1');
        } else {
          arrow.removeAttribute('aria-disabled');
          arrow.removeAttribute('tabindex');
        }
      });
    }

    getFocusableElements() {
      const dialog = this.querySelector('.bundle-qv-dialog') || this.querySelector('[data-qv-dialog]');
      if (!dialog) return [];

      const selector = [
        'a[href]',
        'area[href]',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'button:not([disabled])',
        'iframe',
        'object',
        'embed',
        '[tabindex]:not([tabindex="-1"])',
        '[contenteditable]'
      ].join(', ');

      const elements = Array.from(dialog.querySelectorAll(selector));

      return elements.filter(el => {
        const style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden') return false;
        if (el.closest('[aria-hidden="true"]') || el.closest('[inert]')) return false;
        if (el.getAttribute('tabindex') === '-1') return false;
        if (el.getAttribute('aria-hidden') === 'true') return false;
        if (el.disabled || el.getAttribute('aria-disabled') === 'true' || el.classList.contains('slick-disabled')) return false;
        return el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0;
      });
    }

    handleKeyDown(event) {
      if (!this.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        this.closeModal();
        return;
      }

      if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && event.target.closest('.bundle-qv-image-stage')) {
        const currentCard = Array.from(this.querySelectorAll('[data-qv-product-card]')).find(c => c.style.display !== 'none');
        if (currentCard) {
          const jQuery = window.jQuery;
          const stageEl = currentCard.querySelector('.bundle-qv-image-stage');
          if (jQuery && stageEl && stageEl.classList.contains('slick-initialized')) {
            event.preventDefault();
            if (event.key === 'ArrowLeft') {
              jQuery(stageEl).slick('slickPrev');
            } else {
              jQuery(stageEl).slick('slickNext');
            }
          }
        }
        return;
      }

      if (event.key === 'Tab') {
        const dialog = this.querySelector('.bundle-qv-dialog') || this.querySelector('[data-qv-dialog]');
        if (!dialog) return;

        const currentCard = Array.from(this.querySelectorAll('[data-qv-product-card]')).find(card => {
          return card.style.display !== 'none' && !card.hidden && !card.hasAttribute('hidden') && card.getAttribute('aria-hidden') !== 'true';
        }) || this.querySelector('[data-qv-product-card]');

        const activeEl = document.activeElement;

        // Custom focus management: after visible image, focus goes on next button
        if (currentCard && activeEl && currentCard.contains(activeEl)) {
          const visibleThumbBtns = Array.from(
            currentCard.querySelectorAll('.bundle-qv-thumbnails .slick-slide.slick-active .bundle-qv-thumb-btn')
          );
          const thumbNextBtn = currentCard.querySelector('.bundle-qv-thumb-next-btn');
          const thumbPrevBtn = currentCard.querySelector('.bundle-qv-thumb-prev-btn');
          const mainNextBtn = currentCard.querySelector('.bundle-qv-main-image-wrap .bundle-qv-next-btn');
          const mainPrevBtn = currentCard.querySelector('.bundle-qv-main-image-wrap .bundle-qv-prev-btn');
          const stageEl = currentCard.querySelector('.bundle-qv-image-stage');

          const isThumbNextDisabled = !thumbNextBtn || thumbNextBtn.disabled || thumbNextBtn.getAttribute('aria-disabled') === 'true' || thumbNextBtn.classList.contains('slick-disabled');
          const isThumbPrevDisabled = !thumbPrevBtn || thumbPrevBtn.disabled || thumbPrevBtn.getAttribute('aria-disabled') === 'true' || thumbPrevBtn.classList.contains('slick-disabled');
          const isMainNextDisabled = !mainNextBtn || mainNextBtn.disabled || mainNextBtn.getAttribute('aria-disabled') === 'true' || mainNextBtn.classList.contains('slick-disabled');
          const isMainPrevDisabled = !mainPrevBtn || mainPrevBtn.disabled || mainPrevBtn.getAttribute('aria-disabled') === 'true' || mainPrevBtn.classList.contains('slick-disabled');

          const lastVisibleThumbBtn = visibleThumbBtns[visibleThumbBtns.length - 1];
          const firstVisibleThumbBtn = visibleThumbBtns[0];

          // Check if focus is on/inside the last visible thumbnail
          const isLastThumb = lastVisibleThumbBtn && (
            activeEl === lastVisibleThumbBtn || 
            lastVisibleThumbBtn.contains(activeEl) || 
            (lastVisibleThumbBtn.closest('.slick-slide') && lastVisibleThumbBtn.closest('.slick-slide').contains(activeEl))
          );

          // Check if focus is on/inside the first visible thumbnail
          const isFirstThumb = firstVisibleThumbBtn && (
            activeEl === firstVisibleThumbBtn || 
            firstVisibleThumbBtn.contains(activeEl) || 
            (firstVisibleThumbBtn.closest('.slick-slide') && firstVisibleThumbBtn.closest('.slick-slide').contains(activeEl))
          );

          // Check if focus is on/inside the stage carousel
          const isStageFocused = stageEl && (activeEl === stageEl || (stageEl.contains(activeEl) && !activeEl.closest('.bundle-qv-nav-btn')));

          if (!event.shiftKey) {
            // Forward Tab
            // 1. From last visible thumbnail -> FOCUS GOES TO THUMB NEXT BUTTON
            if (isLastThumb) {
              if (thumbNextBtn && !isThumbNextDisabled) {
                event.preventDefault();
                thumbNextBtn.focus();
                return;
              } else if (mainPrevBtn && !isMainPrevDisabled) {
                event.preventDefault();
                mainPrevBtn.focus();
                return;
              } else if (stageEl && stageEl.getAttribute('tabindex') === '0') {
                event.preventDefault();
                stageEl.focus();
                return;
              } else if (mainNextBtn && !isMainNextDisabled) {
                event.preventDefault();
                mainNextBtn.focus();
                return;
              }
            }

            // 2. From thumb next button -> FOCUS GOES TO MAIN PREV BUTTON or STAGE
            if (thumbNextBtn && (activeEl === thumbNextBtn || thumbNextBtn.contains(activeEl))) {
              if (mainPrevBtn && !isMainPrevDisabled) {
                event.preventDefault();
                mainPrevBtn.focus();
                return;
              } else if (stageEl && stageEl.getAttribute('tabindex') === '0') {
                event.preventDefault();
                stageEl.focus();
                return;
              } else if (mainNextBtn && !isMainNextDisabled) {
                event.preventDefault();
                mainNextBtn.focus();
                return;
              }
            }

            // 3. From main prev button -> FOCUS GOES TO STAGE or MAIN NEXT BUTTON
            if (mainPrevBtn && (activeEl === mainPrevBtn || mainPrevBtn.contains(activeEl))) {
              if (stageEl && stageEl.getAttribute('tabindex') === '0') {
                event.preventDefault();
                stageEl.focus();
                return;
              } else if (mainNextBtn && !isMainNextDisabled) {
                event.preventDefault();
                mainNextBtn.focus();
                return;
              }
            }

            // 4. From stage carousel -> FOCUS GOES TO MAIN NEXT BUTTON
            if (isStageFocused) {
              if (mainNextBtn && !isMainNextDisabled) {
                event.preventDefault();
                mainNextBtn.focus();
                return;
              }
            }
          } else {
            // Reverse Tab (Shift + Tab)
            // 1. From main next button -> back to stage or main prev button or thumb next button
            if (mainNextBtn && (activeEl === mainNextBtn || mainNextBtn.contains(activeEl))) {
              if (stageEl && stageEl.getAttribute('tabindex') === '0') {
                event.preventDefault();
                stageEl.focus();
                return;
              } else if (mainPrevBtn && !isMainPrevDisabled) {
                event.preventDefault();
                mainPrevBtn.focus();
                return;
              } else if (thumbNextBtn && !isThumbNextDisabled) {
                event.preventDefault();
                thumbNextBtn.focus();
                return;
              } else if (lastVisibleThumbBtn) {
                event.preventDefault();
                lastVisibleThumbBtn.focus();
                return;
              }
            }

            // 2. From stage carousel -> back to main prev button or thumb next button or last thumbnail
            if (isStageFocused) {
              if (mainPrevBtn && !isMainPrevDisabled) {
                event.preventDefault();
                mainPrevBtn.focus();
                return;
              } else if (thumbNextBtn && !isThumbNextDisabled) {
                event.preventDefault();
                thumbNextBtn.focus();
                return;
              } else if (lastVisibleThumbBtn) {
                event.preventDefault();
                lastVisibleThumbBtn.focus();
                return;
              }
            }

            // 3. From main prev button -> back to thumb next button or last thumbnail
            if (mainPrevBtn && (activeEl === mainPrevBtn || mainPrevBtn.contains(activeEl))) {
              if (thumbNextBtn && !isThumbNextDisabled) {
                event.preventDefault();
                thumbNextBtn.focus();
                return;
              } else if (lastVisibleThumbBtn) {
                event.preventDefault();
                lastVisibleThumbBtn.focus();
                return;
              }
            }

            // 4. From thumb next button -> back to last visible thumbnail
            if (thumbNextBtn && (activeEl === thumbNextBtn || thumbNextBtn.contains(activeEl))) {
              if (lastVisibleThumbBtn) {
                event.preventDefault();
                lastVisibleThumbBtn.focus();
                return;
              }
            }

            // 5. From first visible thumbnail -> to thumb prev button or close button
            if (isFirstThumb) {
              if (thumbPrevBtn && !isThumbPrevDisabled) {
                event.preventDefault();
                thumbPrevBtn.focus();
                return;
              } else {
                const closeBtn = this.querySelector('.bundle-qv-close');
                if (closeBtn) {
                  event.preventDefault();
                  closeBtn.focus();
                  return;
                }
              }
            }

            // 6. From thumb prev button -> to close button
            if (thumbPrevBtn && (activeEl === thumbPrevBtn || thumbPrevBtn.contains(activeEl))) {
              const closeBtn = this.querySelector('.bundle-qv-close');
              if (closeBtn) {
                event.preventDefault();
                closeBtn.focus();
                return;
              }
            }
          }
        }

        const focusables = this.getFocusableElements();
        if (focusables.length === 0) {
          event.preventDefault();
          dialog.focus();
          return;
        }

        const firstFocusable = focusables[0];
        const lastFocusable = focusables[focusables.length - 1];

        if (!dialog.contains(document.activeElement)) {
          event.preventDefault();
          firstFocusable.focus();
          return;
        }

        if (event.shiftKey) {
          if (document.activeElement === firstFocusable) {
            event.preventDefault();
            lastFocusable.focus();
          }
        } else {
          if (document.activeElement === lastFocusable) {
            event.preventDefault();
            firstFocusable.focus();
          }
        }
      }
    }

    handleFocusIn(event) {
      if (!this.classList.contains('is-open')) return;
      const dialog = this.querySelector('.bundle-qv-dialog') || this.querySelector('[data-qv-dialog]');
      if (!dialog) return;

      if (!dialog.contains(event.target)) {
        const focusables = this.getFocusableElements();
        if (focusables.length > 0) {
          focusables[0].focus();
        } else {
          dialog.focus();
        }
        return;
      }

      // Safety trap: prevent focus from ever lingering on hidden content (inactive slides)
      if (event.target.closest('[aria-hidden="true"], [inert], .slick-slide:not(.slick-active):not(.slick-current)')) {
        const currentCard = Array.from(this.querySelectorAll('[data-qv-product-card]')).find(c => c.style.display !== 'none') || this;
        const thumbNextBtn = currentCard.querySelector('.bundle-qv-thumb-next-btn');
        const mainNextBtn = currentCard.querySelector('.bundle-qv-main-image-wrap .bundle-qv-next-btn');

        if (event.target.closest('.bundle-qv-thumbnails') && thumbNextBtn && !thumbNextBtn.classList.contains('slick-disabled')) {
          thumbNextBtn.focus();
        } else if (event.target.closest('.bundle-qv-image-stage') && mainNextBtn && !mainNextBtn.classList.contains('slick-disabled')) {
          mainNextBtn.focus();
        }
      }
    }

    handleDocumentClick(event) {
      const triggerBtn = event.target.closest('[data-bundle-qv-trigger="true"], .whats-in-bundle-qv-btn, .whats-in-bundle-item-btn, .whats-in-bundle-item');
      if (triggerBtn) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const productId = triggerBtn.getAttribute('data-product-id') || triggerBtn.dataset.productId;
        const handle = triggerBtn.getAttribute('data-id') || triggerBtn.dataset.id;
        const varId = triggerBtn.getAttribute('data-var-id') || triggerBtn.dataset.varId;

        this.openQuickView(productId, handle, varId, triggerBtn);
      }
    }

    openQuickView(productId, handle, varId, triggerBtn) {
      this.lastActiveElement = triggerBtn || document.activeElement;

      const cards = Array.from(this.querySelectorAll('[data-qv-product-card]'));
      
      let targetCard = cards.find(card => varId && String(card.getAttribute('data-qv-var-id')) === String(varId));
      if (!targetCard) {
        targetCard = cards.find(card => 
          (productId && String(card.getAttribute('data-qv-product-card')) === String(productId)) ||
          (handle && card.getAttribute('data-qv-handle') === handle)
        );
      }

      const modalTitle = this.querySelector('[data-qv-modal-title]');
      cards.forEach(card => {
        const titleEl = card.querySelector('.bundle-qv-title');
        if (card === targetCard) {
          card.style.display = 'block';
          card.removeAttribute('aria-hidden');
          card.removeAttribute('hidden');
          if (modalTitle && titleEl) {
            modalTitle.textContent = titleEl.textContent.trim();
          }

          this.initCardSliders(card);

          const ratingWrapper = card.querySelector('.bundle-qv-rating-wrapper');
          if (ratingWrapper) {
            const hasReviewContent = ratingWrapper.querySelector('.review-container') !== null;
            ratingWrapper.style.display = hasReviewContent ? 'block' : 'none';
          }
        } else {
          card.style.display = 'none';
          card.setAttribute('aria-hidden', 'true');
          card.setAttribute('hidden', '');
        }
      });

      if (targetCard) {
        this.openModal();
      }
    }

    openModal() {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      clearTimeout(this._focusTimer);
      this._focusTimer = setTimeout(() => {
        const closeBtn = this.querySelector('.bundle-qv-close');
        if (closeBtn) {
          closeBtn.focus();
        } else {
          const focusables = this.getFocusableElements();
          if (focusables.length > 0) {
            focusables[0].focus();
          } else {
            const dialog = this.querySelector('.bundle-qv-dialog') || this.querySelector('[data-qv-dialog]');
            if (dialog) dialog.focus();
          }
        }
      }, 50);
    }

    closeModal() {
      clearTimeout(this._focusTimer);
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';

      const cards = this.querySelectorAll('[data-qv-product-card]');
      cards.forEach(card => {
        card.style.display = 'none';
        card.setAttribute('aria-hidden', 'true');
        card.setAttribute('hidden', '');
      });

      const modalTitle = this.querySelector('[data-qv-modal-title]');
      if (modalTitle) {
        modalTitle.textContent = 'Product Quick View';
      }

      if (this.lastActiveElement && typeof this.lastActiveElement.focus === 'function') {
        this.lastActiveElement.focus();
      }
    }
  }

  customElements.define('bundle-quick-view', BundleQuickView);
}
